import type { AxiosResponse } from 'axios'
import branditRails from '../../api/brandit_rails'

export const DP_PREVIEW_POLL_INTERVAL_MS = 2000
export const DP_PREVIEW_MAX_POLL_ATTEMPTS = 45

export const DP_PREVIEW_POLL_FAILED_MESSAGE = 'Proof generation failed. Please try again.'
export const DP_PREVIEW_POLL_TIMEOUT_MESSAGE = 'Proof generation timed out. Please try again.'

interface DpPreviewFilePayload {
  url: string
  format?: string
  page_dimensions?: {
    width?: number | null
    height?: number | null
    unit?: string | null
  } | null
}

export interface DpPreviewInitialPayload {
  id: number
  proof_status?: string
  preview?: DpPreviewFilePayload | null
  items_in_stock?: boolean
  [key: string]: unknown
}

export interface DpPreviewStatusPayload {
  proof_status: string
  preview?: DpPreviewFilePayload | null
  [key: string]: unknown
}

export type SubmitDpPreviewResult =
  | { type: 'in_stock' }
  | { type: 'sync'; preview: DpPreviewInitialPayload }
  | { type: 'async'; initial: DpPreviewInitialPayload; productId: number }

export function isAbortError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const err = error as { name?: string; code?: string }
    return err.name === 'AbortError' || err.code === 'ERR_CANCELED'
  }
  return false
}

export function isAsyncDpPreviewResponse(
  response: Pick<AxiosResponse<DpPreviewInitialPayload>, 'status' | 'data'>
): boolean {
  return (
    response.status === 202 ||
    (response.data?.proof_status === 'pending' &&
      (response.data?.preview === null || response.data?.preview === undefined))
  )
}

export async function submitDpPreview(data: FormData | object, signal?: AbortSignal): Promise<SubmitDpPreviewResult> {
  const response = await branditRails.post<DpPreviewInitialPayload>(`/digital_proofing/dp_preview`, data, { signal })

  if (response.data.items_in_stock) {
    return { type: 'in_stock' }
  }

  if (isAsyncDpPreviewResponse(response)) {
    return {
      type: 'async',
      initial: response.data,
      productId: response.data.id
    }
  }

  return { type: 'sync', preview: response.data }
}

export async function fetchDpPreviewStatus(
  digitalProofingProductId: number,
  signal?: AbortSignal
): Promise<DpPreviewStatusPayload> {
  const response = await branditRails.get<DpPreviewStatusPayload>(
    `/digital_proofing_products/${digitalProofingProductId}/preview_status`,
    { signal }
  )

  if (response.data.proof_status === 'failed') {
    throw new Error(DP_PREVIEW_POLL_FAILED_MESSAGE)
  }

  return response.data
}
